import React, { useState } from "react";
import { US_MAP_VIEWBOX, STATE_NAMES, STATE_PATHS } from "../assets/usStatePaths";

// Technology categories (May 2026 EIA-860M operating-generator inventory classification).
export const CATEGORIES = {
  wsb:  { label: "Wind + Solar + Battery", color: "#2FA36B", wind: true,  solar: true,  battery: true  },
  ws:   { label: "Wind + Solar",           color: "#3B78B4", wind: true,  solar: true,  battery: false },
  sb:   { label: "Solar + Battery",        color: "#C89B3C", wind: false, solar: true,  battery: true  },
  w:    { label: "Wind only",              color: "#2D9C9C", wind: true,  solar: false, battery: false },
  s:    { label: "Solar only",             color: "#D9772B", wind: false, solar: true,  battery: false },
  b:    { label: "Battery only",           color: "#7A5CC7", wind: false, solar: false, battery: true  },
  none: { label: "No verified activity",   color: "#253243", wind: false, solar: false, battery: false },
};

// All 50 states classified, no state left blank.
export const STATE_CATEGORY = {
  ak: "wsb", az: "wsb", ar: "wsb", ca: "wsb", co: "wsb", ct: "wsb", hi: "wsb", id: "wsb",
  il: "wsb", in: "wsb", ia: "wsb", me: "wsb", md: "wsb", ma: "wsb", mi: "wsb", mn: "wsb",
  ms: "wsb", ne: "wsb", nv: "wsb", nh: "wsb", nj: "wsb", nm: "wsb", ny: "wsb",
  nc: "wsb", oh: "wsb", ok: "wsb", or: "wsb", pa: "wsb", ri: "wsb", sd: "wsb", tx: "wsb",
  ut: "wsb", vt: "wsb", va: "wsb", wa: "wsb", wv: "wsb", wi: "wsb",
  de: "ws", ks: "ws", mo: "ws", mt: "ws", wy: "ws",
  al: "sb", fl: "sb", ga: "sb", sc: "sb",
  ky: "s", la: "s", tn: "s",
  nd: "w",
};

const yn = (b) => (b ? "Yes" : "No");
const stateDetail = (abbr) => {
  const c = CATEGORIES[STATE_CATEGORY[abbr]];
  return `${STATE_NAMES[abbr]} · Wind: ${yn(c.wind)} · Solar: ${yn(c.solar)} · Battery storage: ${yn(c.battery)}`;
};

export const TalentCoverageMap = () => {
  const [hovered, setHovered] = useState(null);
  const [locked, setLocked] = useState(null);
  const shown = hovered || locked;

  const handleContainerClick = (e) => {
    if (e.target.tagName !== "path") setLocked(null);
  };

  return (
    <div data-testid="talent-coverage-map">
      <div
        className="rounded-lg border p-4 sm:p-6"
        style={{ borderColor: "rgba(244,241,236,0.12)", background: "rgba(11,29,52,0.35)", overflow: "hidden" }}
        onClick={handleContainerClick}
      >
        <svg
          viewBox={US_MAP_VIEWBOX}
          role="img"
          aria-label="Geographic map of the United States showing the operating utility-scale renewable technology mix in each state, categorized from the May 2026 EIA-860M inventory as wind, solar, and battery-storage generators reported with operating status."
          style={{ width: "100%", height: "auto", display: "block", maxWidth: "860px", margin: "0 auto" }}
        >
          <title>Operating utility-scale renewable technology mix by state</title>
          {Object.entries(STATE_PATHS).map(([abbr, d]) => {
            const cat = CATEGORIES[STATE_CATEGORY[abbr] || "none"];
            const isShown = shown && shown.abbr === abbr;
            return (
              <path
                key={abbr}
                d={d}
                data-testid={`map-state-${abbr}`}
                data-category={STATE_CATEGORY[abbr] || "none"}
                fill={cat.color}
                stroke={isShown ? "#F4F1EC" : "#0E0F14"}
                strokeWidth={isShown ? 1.8 : 1.1}
                strokeLinejoin="round"
                tabIndex={0}
                role="img"
                aria-label={stateDetail(abbr)}
                onMouseEnter={() => setHovered({ abbr })}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered({ abbr })}
                onBlur={() => setHovered(null)}
                onClick={() => setLocked((prev) => (prev && prev.abbr === abbr ? null : { abbr }))}
                style={{ cursor: "pointer", outline: "none", transition: "stroke 120ms ease" }}
              />
            );
          })}
        </svg>

        {/* Selected-state details (hover, keyboard focus, or tap-locked) */}
        <p
          aria-live="polite"
          data-testid="map-active-state"
          className="text-center text-[14px] font-semibold mt-3 min-h-[22px]"
          style={{ color: "#F4F1EC" }}
        >
          {shown ? stateDetail(shown.abbr) : ""}
        </p>

        {/* Legend */}
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-2" data-testid="map-legend">
          {["wsb", "ws", "sb", "w", "s"].map((k) => (
            <span key={k} className="inline-flex items-center gap-2 text-[13px]" style={{ color: "#dfe4ea" }}>
              <span className="inline-block w-4 h-4 rounded-sm" style={{ background: CATEGORIES[k].color }} />
              {CATEGORIES[k].label}
            </span>
          ))}
          <span className="inline-flex items-center gap-2 text-[12px]" style={{ color: "#8d99a9" }}>
            <span className="inline-block w-3.5 h-3.5 rounded-sm" style={{ background: CATEGORIES.b.color, opacity: 0.65 }} />
            Battery only: no states currently classified as battery-only
          </span>
        </div>
        <p className="text-center text-[12px] mt-3" style={{ color: "#8d99a9" }}>
          Select a state to view its operating technology mix.
        </p>
        <p className="text-center text-[12px] mt-1" style={{ color: "#8d99a9" }}>
          Wind = operating utility-scale wind generation · Solar = operating utility-scale solar
          generation · Battery = operating grid-scale battery storage
        </p>
      </div>

      {/* Accessible grouped list */}
      <div className="mt-6" data-testid="map-state-list">
        {["wsb", "ws", "sb", "s", "w"].map((k) => {
          const states = Object.keys(STATE_CATEGORY).filter((a) => STATE_CATEGORY[a] === k).map((a) => STATE_NAMES[a]).sort();
          return (
            <div key={k} className="mb-4">
              <p className="text-[12.5px] font-semibold uppercase tracking-[1.5px] mb-2 flex items-center gap-2" style={{ color: "#dfe4ea" }}>
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: CATEGORIES[k].color }} />
                {CATEGORIES[k].label} ({states.length})
              </p>
              <p className="text-[13.5px] leading-relaxed" style={{ color: "#a7b0bc" }}>{states.join(", ")}</p>
            </div>
          );
        })}
      </div>

      <p className="text-[14px] mt-6 font-medium" style={{ color: "#c7cfda" }} data-testid="map-lower48-note">
        Pipeline, transmission, right-of-way, and land-project support is available throughout the
        lower 48 states.
      </p>

      <p className="text-[12px] italic mt-4" style={{ color: "#8d99a9" }} data-testid="map-data-note">
        Technology categories are based on the May 2026 EIA-860M inventory and include units reported
        with operating status at electric power plants meeting the EIA utility-scale reporting
        threshold. The inventory is preliminary and may be revised.
      </p>
    </div>
  );
};

export default TalentCoverageMap;
